// src/pages/admin/ManageOrders.jsx
import React, { useState, useEffect } from 'react';
import { db } from '../../services/firebase';
import { collection, getDocs, updateDoc, doc, query, orderBy } from 'firebase/firestore';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import '../../styles/admin/ManageOrders.css';

const ManageOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        const orderList = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(), // Safe Timestamp conversion
          };
        });
        setOrders(orderList);
        console.log("Fetched orders:", orderList); // Debug log
        if (orderList.length === 0) {
          setFetchError("No orders found. Check if orders are being saved in PaymentSuccessPage.");
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
        setFetchError(`Failed to fetch orders: ${error.message}. Check Firestore rules or console for details.`);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, { status: newStatus });
      setOrders(prev =>
        prev.map(order =>
          order.id === orderId ? { ...order, status: newStatus } : order
        )
      );
      alert(`Order status updated to ${newStatus}`);
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status");
    }
  };

  const toggleExpand = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const filteredOrders = statusFilter === 'all'
    ? orders
    : orders.filter(o => o.status === statusFilter);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="manage-orders">
      <h1>Manage Orders</h1>

      {/* Filter */}
      <div className="filter-bar">
        <label>Filter by Status:</label>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All</option>
          <option value="Pending">Pending</option>
          <option value="Processing">Processing</option>
          <option value="Shipped">Shipped</option>
          <option value="Delivered">Delivered</option>
          <option value="Cancelled">Cancelled</option>
        </select>
      </div>

      {fetchError && <p className="error-message">{fetchError}</p>}

      {/* Orders Table */}
      <div className="orders-table">
        <div className="table-header">
          <div>Order ID</div>
          <div>Customer</div>
          <div>Total</div>
          <div>Status</div>
          <div>Date</div>
          <div>Actions</div>
        </div>

        {filteredOrders.length === 0 ? (
          <p className="no-orders">No orders found. Try placing a test order as a customer.</p>
        ) : (
          filteredOrders.map(order => (
            <React.Fragment key={order.id}>
              <div
                className={`order-row ${expandedOrder === order.id ? 'expanded' : ''}`}
                onClick={() => toggleExpand(order.id)}
              >
                <div>#{order.id.slice(0, 8)}</div>
                <div>{order.customerName || order.userEmail || 'Guest'}</div>
                <div>${order.total?.toFixed(2) || '—'}</div>
                <div className={`status-badge ${order.status?.toLowerCase()}`}>
                  {order.status || 'Pending'}
                </div>
                <div>{order.createdAt.toLocaleDateString()}</div>
                <div>View Details</div>
              </div>

              {expandedOrder === order.id && (
                <div className="order-details">
                  <div className="details-grid">
                    <div>
                      <strong>Customer Email:</strong> {order.userEmail || '—'}
                    </div>
                    <div>
                      <strong>Address:</strong> {order.shippingAddress || '—'}
                    </div>
                    <div>
                      <strong>Payment Method:</strong> {order.paymentMethod || '—'}
                    </div>
                    <div>
                      <strong>Status:</strong>
                      <select
                        value={order.status || 'Pending'}
                        onChange={e => updateOrderStatus(order.id, e.target.value)}
                        className="status-select"
                      >
                        <option value="Pending">Pending</option>
                        <option value="Processing">Processing</option>
                        <option value="Shipped">Shipped</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>

                  <h4>Ordered Items</h4>
                  <div className="order-items">
                    {order.items?.map((item, idx) => (
                      <div key={idx} className="order-item">
                        <img src={item.imageUrl} alt={item.title} className="item-img" />
                        <div>
                          <strong>{item.title}</strong>
                          <p>Qty: {item.quantity} × ${item.price?.toFixed(2)}</p>
                        </div>
                      </div>
                    )) || <p>No items</p>}
                  </div>
                </div>
              )}
            </React.Fragment>
          ))
        )}
      </div>
    </div>
  );
};

export default ManageOrders;